import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { IconButton, Collapse } from '@mui/material';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from "@mui/icons-material/Close";

import Button from '@mui/material/Button';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import AddModal from '../../components/AddModal';
import AssignMultipleModal from '../../components/AssignMultipleModal';
import UploadModal from '../../components/UploadModal';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL } from '../../common/AppConstants';

const TITLE = 'Student Groups';
const API_URL = 'academics/section/';
const SUBJECT_API_URL = 'academics/section-subject/'; // Constant for the Section Subject API endpoint
const SUBJECT_BULK_API_URL = 'academics/section-subject/bulk/';
const ELECTIVE_API_URL = 'academics/section-elective/'; // Constant for the Section Elective API endpoint
const UPLOAD_TEMPLATE = 'student_group_upload_template.xlsx';
const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding now !!!';

const subjectAssignColumn = {
  attr: 'subject',
  editable: true,
  extra: false,
  label: 'Subjects',
  related: true,
  required: true,
  type: 'multiple_choice',
  help_text: 'Subjects',
};

const electiveAssignColumns = [
  {
    attr: 'elective',
    editable: true,
    extra: false,
    label: 'Elective',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Elective subject group',
  },
];

function preventDefault(event) {
  event.preventDefault();
}

export default function Students() {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    relatedData.class_level[row_item.class_level]?.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    relatedData.section_type[row_item.section_type]?.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (`${relatedData.class_level[row_item.class_level]?.label} ${relatedData.section_type[row_item.section_type]?.label}`).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleEditClick = (row) => {
    console.log('Edit row ', row);
    setSelectedEditRow(row);
  };

  useEffect(() => {
    if (selectedEditRow !== null && selectedEditRow !== undefined) {
      // Only open the modal after the row has been selected
      setIsEditModalOpen(true);
    }
  }, [selectedEditRow]); // Runs when selectedEditRow changes

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditRow(null);
  };

  const handleEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.map((row, index) =>
            row.id === selectedEditRow.id ? { ...row, ...data } : row,
          ),
        );
        handleEditModalClose();
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleDelete = async (deletedRow) => {
    await httpRequest({
      method: 'DELETE',
      url: API_URL,
      data: deletedRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.filter((row) => row.id !== deletedRow.id),
        );
      },
      addNotification,
    });
  };

  const handleAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
    await httpRequest({
      method: 'POST',
      url: API_URL,
      data: addFormData,
      onSuccess: (data) => {
        handleAddModalClose();
        setRows((prevRows) => [data.entry, ...prevRows]);
        handleAddClick();
        onAddSuccess();
      },
      onFailure: (data) => {
        onAddFailure(data);
      },
      addNotification,
    });
  };

  const handleUpload = async (file, onUploadSuccess, onUploadFailure) => {
    const formData = new FormData();
    formData.append('file', file);

    await httpRequest({
      method: 'POST',
      url: `${API_URL}upload/`,
      data: formData,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      onSuccess: (data) => {
        refreshData();
        onUploadSuccess(data);
      },
      onFailure: (data) => {
        onUploadFailure(data);
      },
      addNotification,
    });
  };

  const handleFilterApply = async () => {
    // ToDo: Filter table data
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      onSuccess: (data) => {
        setColumns(data.columns);
        setRows(data.rows);
        setRelatedData(data.relatedData);
      },
      onFailure: (data) => {
        setColumns([]);
        setRows([]);
        setRelatedData({});
      },
      addNotification,
    });

    setIsLoading(false);
  };

  const SectionSubjectCard = ({ section_row, section_dict }) => {
    // const incomplete_data = section_dict['subject_list'].length === 0;
    const [expanded, setExpanded] = useState(false);  // incomplete_data
    const [selectedRemoveSubject, setSelectedRemoveSubject] = useState(null);
    const [isSubjectAddModalOpen, setIsSubjectAddModalOpen] = useState(false);
    const [selectedRemoveElective, setSelectedRemoveElective] = useState(null);
    const [isElectiveAddModalOpen, setIsElectiveAddModalOpen] = useState(false);

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };

    const handleSubjectAssignModalClose = () => {
      setIsSubjectAddModalOpen(false);
    };

    const handleElectiveAssignModalClose = () => {
      setIsElectiveAddModalOpen(false);
    };

    const handleSubjectAssignSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const finalFormData = {
        subject_list: addFormData,
        section: section_dict.id,
      };
      
      await httpRequest({
        method: 'PUT',
        url: SUBJECT_BULK_API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === section_dict.id
                ? {
                    ...row,
                    subject_list: data.subject_list,
                  }
                : row,
            ),
          );
          // setIsSubjectAddModalOpen(false);
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    const handleElectiveAssignSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const finalFormData = {
        ...addFormData,
        section: section_dict.id,
      };
      
      await httpRequest({
        method: 'POST',
        url: ELECTIVE_API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === section_dict.id
                ? {
                    ...row,
                    elective_list: [
                      ...row.elective_list,
                      data.entry
                    ],
                  }
                : row,
            ),
          );
          // setIsElectiveAddModalOpen(false);
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    const closeSubjectDeleteConfirmation = () => {
      setSelectedRemoveSubject(null);
    };

    const closeElectiveDeleteConfirmation = () => {
      setSelectedRemoveElective(null);
    };

    const handleRemoveSubject = async () => {
      await httpRequest({
        method: 'DELETE',
        url: SUBJECT_API_URL,
        data: {
          id: selectedRemoveSubject.id,
        },
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === section_dict.id
                ? {
                    ...row,
                    subject_list: row.subject_list.filter(
                      (subject_comp) => subject_comp.id !== selectedRemoveSubject.id,
                    ),
                  }
                : row,
            ),
          );
          setSelectedRemoveSubject(null);
        },
        addNotification,
      });
    };

    const handleRemoveElective = async () => {
      await httpRequest({
        method: 'DELETE',
        url: ELECTIVE_API_URL,
        data: {
          id: selectedRemoveElective.id,
        },
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === section_dict.id
                ? {
                    ...row,
                    elective_list: row.elective_list.filter(
                      (elective_comp) => elective_comp.id !== selectedRemoveElective.id,
                    ),
                  }
                : row,
            ),
          );
          setSelectedRemoveElective(null);
        },
        addNotification,
      });
    };

    const SubjectChip = ({ section_row, subject_dict }) => (
      <Chip
        label={relatedData['subject'][subject_dict['subject']]['label']}
        color="primary"
        variant="outlined"
        onDelete={() =>
          setSelectedRemoveSubject({
            subject: subject_dict.subject,
            id: subject_dict['id'],
          })
        }
        deleteIcon={<CancelIcon />}
      />
    );

    const ElectiveChip = ({ section_row, elective_dict }) => (
      <Chip
        label={relatedData['elective'][elective_dict['elective']]['label']}
        color="primary"
        variant="outlined"
        onDelete={() =>
          setSelectedRemoveElective({
            elective: elective_dict.elective,
            id: elective_dict['id'],
          })
        }
        deleteIcon={<CancelIcon />}
      />
    );

    return (
      <Box>
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            }
          }
        }>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5">{section_dict['name']}</Typography>
                {/* More data chips if needed */}
                <Tooltip title="Shift" arrow>
                  <Chip
                    size="small"
                    label={relatedData?.shift?.[section_dict?.shift]?.name || NA_LABEL}
                    variant="outlined"
                    sx={{
                      maxHeight: 18,
                      minWidth: 36,
                      fontSize: '0.8rem',
                    }}
                  />
                </Tooltip>
              </Box>
            }
            action={
              <IconButton onClick={() => handleEditClick(section_dict)} size="small">
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              px: 2,
              py: 0,
              alignItems: 'center',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            slotProps={{
              content: { sx: { py: 1.5 } },
            }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.75, pt: 2, pb: 2 }}>
              {/* <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Department:</Typography> {relatedData?.['department']?.[section_dict['department']]?.['label'] ?? NA_LABEL}
              </Typography> */}
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Class Teacher:</Typography> {relatedData?.['class_teacher']?.[section_dict['class_teacher']]?.['label'] ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Class Room:</Typography> {relatedData?.['classroom']?.[section_dict['classroom']]?.['label'] ?? NA_LABEL}
              </Typography>
              {/* <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Headcount:</Typography> {section_dict['headcount'] ?? NA_LABEL}
              </Typography> */}
            </Box>
          </CardContent>
        
          {/* Collapsible Content */}
          <Collapse in={expanded}>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <CardContent sx={{ bgcolor: '#FBFBFB' }}>
              <Stack spacing={1}>
                {/* Subjects Assignment Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" alignContent={'center'}>
                    <Typography component="span" variant="body2" fontWeight="bold" sx={{ mr: 1 }}>Subjects to be taught</Typography>
                  </Typography>
                  {/* Assign Subject Chip */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setIsSubjectAddModalOpen(true)}
                    // sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
                  >
                    Assign subject
                  </Button>
                </Box>


                <Divider sx={{ borderStyle: 'dashed' }} />

                {/* Electives Assignment Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" alignContent={'center'}>
                    <Typography component="span" variant="body2" fontWeight="bold" sx={{ mr: 1 }}>Electives to be taught</Typography>
                  </Typography>
                  {/* Assign Elective Chip */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setIsElectiveAddModalOpen(true)}
                    // sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
                  >
                    Assign elective
                  </Button>
                </Box>

                {/* Elective Chips */}
                
              </Stack>
            </CardContent>
          </Collapse>
        </Card>
        
        {/* Assign Subject Modal */}
        <Box>
          <AssignMultipleModal
            open={isSubjectAddModalOpen}
            column={subjectAssignColumn}
            relatedData={relatedData}
            rows={section_dict.subject_list}
            handleClose={handleSubjectAssignModalClose}
            handleSave={handleSubjectAssignSave}
            add_title={`${section_dict.name} : Assign Subjects`}
          />
        </Box>

        {/* Assign Elective Modal */}
        <Box>
          <AddModal
            open={isElectiveAddModalOpen}
            columns={electiveAssignColumns}
            relatedData={relatedData}
            rows={section_dict.elective_list}
            handleClose={handleElectiveAssignModalClose}
            handleSave={handleElectiveAssignSave}
            add_title={`${section_dict.name} : Assign Elective`}
          />
        </Box>

        {/* Remove Subject Assignment */}
        <DeleteConfirmationDialog
          open={selectedRemoveSubject !== null}
          onClose={closeSubjectDeleteConfirmation}
          onConfirm={handleRemoveSubject}
          action='Remove'
          text={
            selectedRemoveSubject
              ? `Are you sure you want to remove '${relatedData.subject?.[selectedRemoveSubject.subject].label}' from '${section_dict.name}'?`
              : 'Are you sure you want to remove this item?'
          }
        />

        {/* Remove Elective Assignment */}
        <DeleteConfirmationDialog
          open={selectedRemoveElective !== null}
          onClose={closeElectiveDeleteConfirmation}
          onConfirm={handleRemoveElective}
          action='Remove'
          text={
            selectedRemoveElective
              ? `Are you sure you want to remove '${relatedData.elective?.[selectedRemoveElective.elective].label}' from '${section_dict.name}'?`
              : 'Are you sure you want to remove this item?'
          }
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {/* <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
              sx={{
                px: 4,
                display: { md: 'flex', xs: 'none' },
              }}
            >
              Upload
            </Button> */}
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleAddClick}
              sx={{
                px: 4,
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <SectionSubjectCard
                      section_row={index}
                      section_dict={item_dict}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Add Section Modal */}
      <Box>
        <AddModal
          open={isAddModalOpen}
          columns={columns}
          relatedData={relatedData}
          rows={rows}
          handleClose={handleAddModalClose}
          handleSave={handleAddSave}
          add_title="Add Student Group"
        />
      </Box>

      {/* Upload Modal */}
      <UploadModal
        open={isUploadModalOpen}
        uploadTemplate={UPLOAD_TEMPLATE}
        handleClose={handleUploadModalClose}
        handleUpload={handleUpload}
      />

      {/* Edit Section Modal */}
      <Box>
        <EditModal
          open={isEditModalOpen}
          columns={columns}
          rowData={selectedEditRow}
          relatedData={relatedData}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          handleDelete={handleDelete}
          edit_title="Edit Student"
        />
      </Box>
    </Box>
  );
}
